import React, { useState, useEffect, useRef, useMemo } from 'react';

// Each option row's pixel height. The list container uses a hard 256px
// max-height (Tailwind `max-h-64`) so at 36px per row we can fit ~7 rows
// at once. We render those + a small overscan above & below the visible
// window, which is enough for smooth scroll without blowing render time
// when the list has hundreds of items.
const ROW_HEIGHT = 36;
const OVERSCAN = 4;
const VIEWPORT_HEIGHT = 256;

const ProjectSelect = ({ value, onChange, options }) => {
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState('');
  const [scrollTop, setScrollTop] = useState(0);
  const containerRef = useRef(null);
  const listRef = useRef(null);

  // Close on outside click. Also reset the filter so the next open
  // shows the full list.
  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
        setFilter('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Reset the scrollTop whenever the filter changes — otherwise an
  // already-scrolled list might show an empty section because the
  // filtered result is shorter than the scroll offset.
  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = 0;
    setScrollTop(0);
  }, [filter]);

  // Pre-compute filtered options once per render. lowercased filter so
  // we don't lowercase each label on every comparison.
  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => o.label.toLowerCase().includes(q));
  }, [options, filter]);

  const selectedLabel = useMemo(
    () => options.find((o) => o.value === value)?.label || '-- select --',
    [options, value],
  );

  // Virtualization math: only render the slice of rows currently
  // visible inside the 256px viewport, plus an overscan buffer above
  // and below. Total height of the list is `filtered.length * ROW_HEIGHT`
  // so the native scrollbar stays accurate.
  const totalHeight = filtered.length * ROW_HEIGHT;
  const startIdx = Math.max(0, Math.floor(scrollTop / ROW_HEIGHT) - OVERSCAN);
  const endIdx = Math.min(
    filtered.length,
    Math.ceil((scrollTop + VIEWPORT_HEIGHT) / ROW_HEIGHT) + OVERSCAN,
  );
  const visibleRows = filtered.slice(startIdx, endIdx);
  const offsetY = startIdx * ROW_HEIGHT;

  return (
    <div className="relative flex-1 min-w-0" ref={containerRef}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full border border-gray-300 rounded px-3 py-2 text-left text-sm bg-white flex items-center justify-between focus:outline-none focus:border-[#6BADE0] focus:ring-1 focus:ring-[#6BADE0]"
        data-testid="project-select-trigger"
      >
        <span className={value ? 'text-gray-800 truncate' : 'text-gray-400'}>{selectedLabel}</span>
        <span className="text-gray-400 text-xs shrink-0 ml-2">&#9650;</span>
      </button>
      {open && (
        <div className="absolute z-[100] w-full mt-1 bg-white border border-[#6BADE0] rounded shadow-lg" data-testid="project-dropdown-panel">
          <div className="p-2 border-b border-gray-200">
            <input
              type="text"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Type to filter…"
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:border-[#6BADE0]"
              autoFocus
              data-testid="project-filter-input"
            />
          </div>
          {/* Fixed-height static row above the virtual list shows the
              "-- select --" no-op option. Keeping it outside the
              virtualized scroll means the option is always reachable
              even with an empty filter. */}
          <ul data-testid="project-options-list" className="border-b border-gray-100">
            <li
              className={`px-3 py-2 text-sm cursor-pointer transition-colors ${value === '' ? 'bg-[#5B9BD5] text-white' : 'text-gray-400 hover:bg-[#5B9BD5] hover:text-white'}`}
              onClick={() => { onChange(''); setOpen(false); setFilter(''); }}
              data-testid="project-option-clear"
            >
              -- select --
            </li>
          </ul>
          {/* Scrollable virtualized region. The outer div is the
              viewport; the inner div is sized to the full list height
              so the scrollbar reflects the true count. A translated
              child holds only the visible slice. */}
          <div
            ref={listRef}
            onScroll={(e) => setScrollTop(e.currentTarget.scrollTop)}
            style={{ maxHeight: VIEWPORT_HEIGHT, overflowY: 'auto' }}
            data-testid="project-options-viewport"
          >
            {filtered.length === 0 ? (
              <div className="px-3 py-3 text-sm text-gray-400 italic" data-testid="project-options-empty">
                No projects match
              </div>
            ) : (
              <div style={{ height: totalHeight, position: 'relative' }}>
                <ul
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    transform: `translateY(${offsetY}px)`,
                  }}
                >
                  {visibleRows.map((opt) => (
                    <li
                      key={opt.value}
                      style={{ height: ROW_HEIGHT, lineHeight: `${ROW_HEIGHT}px` }}
                      className={`px-3 text-sm cursor-pointer transition-colors truncate ${value === opt.value ? 'bg-[#5B9BD5] text-white' : 'text-gray-700 hover:bg-[#5B9BD5] hover:text-white'}`}
                      onClick={() => { onChange(opt.value); setOpen(false); setFilter(''); }}
                      data-testid={`project-option-${opt.value}`}
                      title={opt.label}
                    >
                      {opt.label}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
          {/* Lightweight count footer — gives the user a quick sense of
              how big the result set is when typing to filter. */}
          {filtered.length > 0 && (
            <div className="px-3 py-1 text-[11px] text-gray-400 border-t border-gray-100 bg-gray-50" data-testid="project-options-count">
              {filtered.length}{filter ? ` matching "${filter}"` : ' projects'}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ProjectSelect;
